import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL, MONTHS, formatDateOnly } from "./constants/adminConstants";
import "./css/adminStyles.css";

function AdminLoginHistory({ user }) {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [monthFilter, setMonthFilter] = useState("");
  const [yearFilter, setYearFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef(null);
  useEffect(() => {
    if (searchOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [searchOpen]);

  const rolePath = user?.role?.toLowerCase() || "admin";

  useEffect(() => {
    fetchHistory();
  }, []);

  useEffect(() => {
    let filtered = [...history];

    if (monthFilter) {
      filtered = filtered.filter((h) => {
        const d = h.loginAt ? new Date(h.loginAt) : null;
        return d && d.getMonth() === parseInt(monthFilter);
      });
    }

    if (yearFilter) {
      filtered = filtered.filter((h) => {
        const d = h.loginAt ? new Date(h.loginAt) : null;
        return d && d.getFullYear() === parseInt(yearFilter);
      });
    }

    if (dateFilter) {
      filtered = filtered.filter((h) => {
        const d = h.loginAt ? new Date(h.loginAt) : null;
        if (!d) return false;
        const ymd = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        return ymd === dateFilter;
      });
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(
        (h) =>
          (h.firstName || "").toLowerCase().includes(q) ||
          (h.lastName || "").toLowerCase().includes(q) ||
          (h.loginId || "").toLowerCase().includes(q) ||
          (h.role || "").toLowerCase().includes(q)
      );
    }

    setFilteredHistory(filtered);
  }, [monthFilter, yearFilter, dateFilter, searchQuery, history]);

  const fetchHistory = async () => {
    const token = sessionStorage.getItem("tvc_token");
    try {
      let endpoint;
      if (user.role === "Admin") endpoint = `${API_BASE_URL}/admin/login-history`;
      else endpoint = `${API_BASE_URL}/manager/login-history`;

      const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok) {
        setHistory(data.history || []);
        setFilteredHistory(data.history || []);
      }
    } catch (err) {
      console.error("Fetch history error:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatTimeOnly = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 20 }, (_, i) => currentYear - i);

  if (loading) {
    return <div className="AdminEmptyState">Loading login history...</div>;
  }

  return (
    <div className="AdminCard">
      <div className="AdminCardHeader">
         <h3 style={{ margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 20, fontWeight: 700, color: "#1B2A4A" }}>Login History <span className="AdminCountBadge">{filteredHistory.length}</span></h3>
        <div className="AdminPillFilterBar">
        <div className="AdminPillGroup">
          <div className={`AdminPillSearchWrap ${searchOpen ? 'is-open' : ''}`}>
            <span className="AdminPillSearchIcon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
            </span>
            <input
              ref={searchRef}
              type="text"
              className="AdminPillSearch"
              placeholder="Search name, role, login ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
               onBlur={() => { setSearchQuery(""); setTimeout(() => setSearchOpen(false), 200) }}
            />
            {!searchOpen && (
              <button className="AdminPillSearchOverlay" onClick={() => setSearchOpen(true)} type="button" aria-label="Search" />
            )}
          </div>
        </div>

        <div className="AdminPillGroup">
          <div className="AdminPillFilterItem">
            <select className="AdminPillSelect" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              <option value="">Months</option>
              {MONTHS.map((m, i) => (
                <option key={i} value={i}>{m}</option>
              ))}
            </select>
            {monthFilter && (
              <button className="AdminPillClear" onClick={() => setMonthFilter("")}>&times;</button>
            )}
          </div>

          <div className="AdminPillFilterItem">
            <select className="AdminPillSelect" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
              <option value="">Years</option>
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            {yearFilter && (
              <button className="AdminPillClear" onClick={() => setYearFilter("")}>&times;</button>
            )}
          </div>
        </div>
      </div>
      </div>

      {filteredHistory.length === 0 ? (
        <div className="AdminEmptyState">No login history found.</div>
      ) : (
        <div className="AdminTableScroll">
        <table className="AdminTable">
          <thead>
            <tr>
              <th>Date</th>
              <th>User</th>
              <th>Login ID</th>
              <th>Role</th>
              <th>Action</th>
              <th>Login Time</th>
              <th>Logout Time</th>
            </tr>
          </thead>
          <tbody>
            {filteredHistory.map((h) => (
              <tr key={h.id}>
                <td>{formatDateOnly(h.loginAt || h.createdAt)}</td>
                <td>
                  {h.firstName} {h.lastName}
                  {h.userId === user.id && <span className="AdminMeTag"> (Me)</span>}
                </td>
                <td>{h.loginId}</td>
                <td>
                  <span className={`AdminRoleIndicator AdminRole${h.role}`}>
                    {h.role}
                  </span>
                </td>
                <td>
                  <span className={`AdminBadge ${h.action === "login" ? "AdminBadgeActive" : "AdminBadgeRejected"}`}>
                    {h.action === "login" ? "Login" : "Logout"}
                  </span>
                </td>
                <td>{formatTimeOnly(h.loginAt)}</td>
                <td>{formatTimeOnly(h.logoutAt)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

export default AdminLoginHistory;